import { Box, Container, Stack, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { changelogEntriesEn } from "widgets/changelog/entries.en"
import { changelogEntriesEs } from "widgets/changelog/entries.es"
import Entry from "widgets/changelog/Entry"
import { IMAGOTYPE } from "helpers/utils/constants"
import PageTitle from "widgets/common/PageTitle"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"

const ENTRIES_PER_PAGE = 10

const Changelog = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage

  useDocumentTitle(t("pageTitles.CHANGELOG"))

  const entries = useMemo(() => {
    return lang === "es" ? changelogEntriesEs : changelogEntriesEn
  }, [lang])

  const [displayedEntries, setDisplayedEntries] = useState(
    entries.slice(0, ENTRIES_PER_PAGE),
  )
  const [hasMore, setHasMore] = useState(entries.length > ENTRIES_PER_PAGE)
  const observerTarget = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(() => {
    if (!hasMore) return

    setDisplayedEntries((prev) => {
      const nextCount = prev.length + ENTRIES_PER_PAGE
      const newEntries = entries.slice(0, nextCount)
      setHasMore(nextCount < entries.length)
      return newEntries
    })
  }, [entries, hasMore])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore()
        }
      },
      { threshold: 0.1 },
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [hasMore, loadMore])

  return (
    <Container maxWidth="lg" sx={{ py: "3rem" }}>
      <Stack
        flexDirection={{ xs: "column-reverse", sm: "row" }}
        justifyContent="space-between"
        gap={4}
        alignItems={"center"}
        mb={4}
      >
        <Stack
          alignItems={{ xs: "center", sm: "flex-start" }}
          textAlign={{ xs: "center", sm: "left" }}
        >
          <PageTitle variant="h1" text={t("pageTitles.CHANGELOG")} styles={{ mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            {t("changelog.SUBTITLE")}
          </Typography>
        </Stack>
        <Box component="img" src={IMAGOTYPE} maxWidth="220px" />
      </Stack>

      <Stack>
        {displayedEntries.map((entry) => (
          <Entry key={entry.release} release={entry.release} changes={entry.changes} />
        ))}
      </Stack>

      {/* Intersection Observer target */}
      {hasMore && <Box ref={observerTarget} sx={{ height: 20 }} />}
    </Container>
  )
}

export default Changelog
